/**
 * Core-area helpers: API error interpretation, data loading, redirects and small browser utilities.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import { ApiRequestError, api } from "../../ui/api.ts";

// ---------------------------------------------------------------- errors

export type FieldErrors = Record<string, string>;

/** Human-readable message for any thrown value (API, network or programming error). */
export function errorMessage(e: unknown): string {
  if (e instanceof ApiRequestError) {
    if (e.code === "owner_test_mode" || e.code === "owner_test_unavailable") return "This action is currently unavailable.";
    if (e.status === 429) return e.message && e.message !== "Too Many Requests" ? e.message : "Too many attempts. Please wait a moment and try again.";
    const ref = typeof e.details?.["requestId"] === "string" ? e.details["requestId"] : null;
    const base = e.message || "The request could not be completed.";
    return e.status >= 500 && ref ? `${base} (Reference: ${ref})` : base;
  }
  if (e instanceof DOMException && e.name === "AbortError") return "The request was cancelled.";
  if (isNetworkError(e)) return "We couldn't reach WorldBet24. Check your internet connection and try again.";
  if (e instanceof Error && e.message) return e.message;
  return "Something went wrong. Please try again.";
}

/** fetch() rejects with a TypeError when the network request itself fails. */
export function isNetworkError(e: unknown): boolean {
  return e instanceof TypeError && /fetch|network|load failed/i.test(e.message);
}

/** 503 + "*_unavailable": an external dependency (licence, provider contract, credential) is not in place. */
export function isUnavailableError(e: unknown): e is ApiRequestError {
  return e instanceof ApiRequestError && e.status === 503 && e.code.endsWith("_unavailable");
}

export function isApiError(e: unknown, code?: string): e is ApiRequestError {
  return e instanceof ApiRequestError && (code === undefined || e.code === code);
}

/** Network failures and 5xx (except *_unavailable) are worth retrying with the same idempotency key. */
export function isRetryable(e: unknown): boolean {
  if (e instanceof ApiRequestError) return e.status >= 500 && !isUnavailableError(e);
  return isNetworkError(e);
}

/**
 * Extracts per-field validation errors from an API error. Supports zod-style `details.issues`
 * ([{ path, message }], path as string or array), `details.fields` ({ name: message }) and the
 * contract's single `details.field` (which then carries the error's own message).
 */
export function fieldErrorsFrom(e: unknown, known?: readonly string[]): FieldErrors {
  const out: FieldErrors = {};
  if (!(e instanceof ApiRequestError) || !e.details) return out;
  const d = e.details;
  const accept = (name: unknown, message: unknown) => {
    const raw = Array.isArray(name) ? name[0] : name;
    if (typeof raw !== "string" || !raw) return;
    const key = raw.split(".")[0] ?? raw;
    if (known && !known.includes(key)) return;
    if (out[key]) return;
    out[key] = typeof message === "string" && message ? message : e.message;
  };
  const issues = d["issues"];
  if (Array.isArray(issues)) {
    for (const issue of issues as unknown[]) {
      if (!issue || typeof issue !== "object") continue;
      const it = issue as { path?: unknown; message?: unknown; field?: unknown };
      accept(it.path ?? it.field, it.message);
    }
  }
  const fields = d["fields"];
  if (fields && typeof fields === "object" && !Array.isArray(fields)) {
    for (const [k, v] of Object.entries(fields as Record<string, unknown>)) accept(k, Array.isArray(v) ? v[0] : v);
  }
  if (typeof d["field"] === "string") accept(d["field"], typeof d["message"] === "string" ? d["message"] : e.message);
  return out;
}

/** Where a refused action can be resolved by the player (shown as a link after the message). */
export function errorAction(e: unknown): { label: string; to: string } | null {
  if (!(e instanceof ApiRequestError)) return null;
  switch (e.code) {
    case "insufficient_funds":
      return { label: "Deposit", to: "/wallet/deposit" };
    case "kyc_required":
      return { label: "Document Verification", to: "/account/verification" };
    case "age_not_verified":
    case "profile_incomplete":
      return { label: "Complete your profile", to: "/complete-profile" };
    case "kyc_rejected":
      return { label: "Contact support", to: "/support" };
    case "deposit_limit":
    case "stake_limit":
    case "loss_limit":
    case "session_limit_break":
      return { label: "Responsible Gaming", to: "/account/responsible-gaming" };
    case "self_excluded":
    case "cooling_off":
      return { label: "Responsible Gaming", to: "/account/take-a-break" };
    case "account_suspended":
    case "account_blocked":
    case "aml_review":
    case "account_restricted":
    case "withdrawals_restricted":
    case "payments_unavailable":
    case "password_reset_unavailable":
    case "username_reminder_unavailable":
      return { label: "Contact support", to: "/support" };
    case "unauthenticated":
      return { label: "Log in", to: `/login?next=${encodeURIComponent(location.pathname + location.search)}` };
    default:
      return null;
  }
}

// ---------------------------------------------------------------- data loading

export interface ApiData<T> {
  data: T | undefined;
  error: unknown;
  loading: boolean;
  reload: () => void;
  setData: Dispatch<SetStateAction<T | undefined>>;
}

/** GETs `path` (null = skip) and exposes data/error/loading with abort-on-unmount and reload. */
export function useApiData<T>(path: string | null): ApiData<T> {
  const [data, setData] = useState<T | undefined>(undefined);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState<boolean>(path !== null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (path === null) {
      setLoading(false);
      return;
    }
    const ctrl = new AbortController();
    setLoading(true);
    setError(null);
    api.get<T>(path, { signal: ctrl.signal }).then(
      (d) => {
        if (ctrl.signal.aborted) return;
        setData(d);
        setLoading(false);
      },
      (e: unknown) => {
        if (ctrl.signal.aborted) return;
        setError(e);
        setLoading(false);
      },
    );
    return () => ctrl.abort();
  }, [path, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { data, error, loading, reload, setData };
}

/** Sets the document title for the current page. */
export function usePageTitle(title: string): void {
  useEffect(() => {
    const prev = document.title;
    document.title = title ? `${title} · WorldBet24` : "WorldBet24";
    return () => {
      document.title = prev;
    };
  }, [title]);
}

/** Calls `fn` every `ms` while `active`; the latest `fn` is always used. */
export function useInterval(fn: () => void, ms: number, active: boolean): void {
  const ref = useRef(fn);
  useEffect(() => {
    ref.current = fn;
  }, [fn]);
  useEffect(() => {
    if (!active) return;
    const id = window.setInterval(() => ref.current(), ms);
    return () => window.clearInterval(id);
  }, [ms, active]);
}

// ---------------------------------------------------------------- navigation

/** Accepts only same-site relative paths for post-login redirects (blocks //evil.example and loops). */
export function safeNext(raw: string | null | undefined, fallback = "/"): string {
  if (!raw) return fallback;
  if (!raw.startsWith("/") || raw.startsWith("//") || raw.startsWith("/\\")) return fallback;
  if (/^\/(login|register|forgot-password|reset-password)(\/|\?|#|$)/.test(raw)) return fallback;
  if (raw.startsWith("/api/")) return fallback;
  return raw;
}

// ---------------------------------------------------------------- browser utilities

/** Saves `data` as a pretty-printed JSON file through a temporary object URL. */
export function downloadJson(data: unknown, filename: string): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 2000);
}

export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/** Local calendar date as YYYY-MM-DD. */
export function localIsoDate(d: Date = new Date()): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Focuses the first element whose id is `${prefix}${field}` for the first field with an error. */
export function focusFirstError(errors: FieldErrors, order: readonly string[], prefix: string): void {
  const first = order.find((f) => errors[f]);
  if (!first) return;
  const el = document.getElementById(`${prefix}${first}`);
  if (el) {
    el.focus({ preventScroll: true });
    el.scrollIntoView({ block: "center" });
  }
}

/** Reads a string/number field from loosely-typed API objects (camelCase or snake_case). */
export function pick(obj: unknown, ...keys: string[]): unknown {
  if (!obj || typeof obj !== "object") return undefined;
  const rec = obj as Record<string, unknown>;
  for (const k of keys) {
    if (rec[k] !== undefined && rec[k] !== null) return rec[k];
  }
  return undefined;
}

export function pickString(obj: unknown, ...keys: string[]): string | null {
  const v = pick(obj, ...keys);
  return typeof v === "string" ? v : typeof v === "number" ? String(v) : null;
}

/** Returns the first array found at `keys` (or `value` itself when it is an array). */
export function pickArray(value: unknown, ...keys: string[]): unknown[] {
  if (Array.isArray(value)) return value;
  const v = pick(value, ...keys);
  return Array.isArray(v) ? v : [];
}
